import { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowLeftIcon,
  LinkIcon,
  LockClosedIcon,
  LockOpenIcon,
  TrashIcon,
  EyeIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentListIcon,
  ArrowPathIcon,
  DocumentIcon,
  FolderPlusIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  ArrowsUpDownIcon,
  CloudArrowUpIcon,
  ChevronRightIcon,
  HomeIcon,
  FolderIcon,
  PlusIcon,
  DocumentDuplicateIcon,
  ClockIcon,
  CheckCircleIcon,
  PauseCircleIcon,
  XCircleIcon,
  BriefcaseIcon,
  PencilIcon,
  SparklesIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { FolderIcon as FolderSolidIcon } from '@heroicons/react/24/solid';
import { useClientStore, type ClientDetail } from '../stores/clientStore';
import { useTemplateStore } from '../stores/templateStore';
import { useUIStore } from '../stores/uiStore';
import { useFileStore } from '../stores/fileStore';
import { clientsApi, portalsApi } from '../api/clients';
import { projectsApi, type ProjectWithStats, type ProjectType, type ProjectStatus } from '../api/projects';
import { foldersApi } from '../api/folders';
import { filesApi } from '../api/files';
import { diagnosticsApi } from '../api/diagnostics';
import type { DiagnosticForm, FormPortal } from '@affogato/shared';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { useToast } from '../components/ui/Toaster';
import { PortalFileSelector } from '../components/clients/PortalFileSelector';
import { GenerateReportModal } from '../components/clients/GenerateReportModal';
import { DocumentationReport } from '../components/clients/DocumentationReport';
import { CreateDocumentModal } from '../components/documents/CreateDocumentModal';
import { MoveToFolderModal } from '../components/files/MoveToFolderModal';
import { ContextMenuWrapper } from '../components/files/ContextMenu';
import { RenameModal } from '../components/files/RenameModal';
import { CreateLinkModal } from '../components/files/CreateLinkModal';
import { DeleteConfirmDialog } from '../components/files/DeleteConfirmDialog';
import { portalReportsApi } from '../api/portal-reports';
import { projectTypeLabels, projectStatusLabels } from '@affogato/shared';
import type { PortalReport } from '@affogato/shared';
import clsx from 'clsx';

// Animation variants - defined outside component to prevent recreation
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.02 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.15 } },
};

interface FolderItem {
  id: string;
  parentId: string | null;
  name: string;
  path: string;
  color?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface FileItem {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  isStarred: boolean;
  createdAt: string;
}

interface BreadcrumbItem {
  id: string | null;
  name: string;
  type: 'client' | 'project' | 'folder';
}

type SortBy = 'name' | 'updatedAt';

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
}

const statusIcons: Record<ProjectStatus, React.ReactNode> = {
  active: <ClockIcon className="w-4 h-4 text-blue-500" />,
  completed: <CheckCircleIcon className="w-4 h-4 text-green-500" />,
  paused: <PauseCircleIcon className="w-4 h-4 text-yellow-500" />,
  cancelled: <XCircleIcon className="w-4 h-4 text-red-500" />,
};

const statusColors: Record<ProjectStatus, string> = {
  active: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  paused: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const typeColors: Record<ProjectType, string> = {
  fiscal: '#ef4444',
  audit: '#8b5cf6',
  payroll: '#10b981',
  consulting: '#3b82f6',
  legal: '#6366f1',
  accounting: '#f59e0b',
  other: '#6b7280',
};

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toastRef = useRef(useToast());

  const { currentClient, setCurrentClient, setPortalFiles, removePortalFile, updateCurrentClientPortal, removeClient } =
    useClientStore();
  const { templates, fetchTemplates, applyTemplate } = useTemplateStore();

  // Global stores for upload modal and context menu
  const openUploadModal = useUIStore((s) => s.openUploadModal);
  const openCreateFolderModal = useUIStore((s) => s.openCreateFolderModal);
  const openContextMenu = useUIStore((s) => s.openContextMenu);
  const isUploadModalOpen = useUIStore((s) => s.isUploadModalOpen);
  const isCreateFolderModalOpen = useUIStore((s) => s.isCreateFolderModalOpen);
  const isDeleteConfirmOpen = useUIStore((s) => s.isDeleteConfirmOpen);
  const isMoveModalOpen = useUIStore((s) => s.isMoveModalOpen);
  const setGlobalCurrentFolderId = useFileStore((s) => s.setCurrentFolderId);
  const setGlobalCurrentProjectId = useFileStore((s) => s.setCurrentProjectId);
  const setGlobalCurrentClientId = useFileStore((s) => s.setCurrentClientId);
  const setGlobalFiles = useFileStore((s) => s.setFiles);

  // Page state
  const [isLoading, setIsLoading] = useState(true);
  const [isPortalMenuOpen, setIsPortalMenuOpen] = useState(false);
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);

  // Projects state
  const [projects, setProjects] = useState<ProjectWithStats[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectWithStats | null>(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);

  // Modal state
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isFileSelectorOpen, setIsFileSelectorOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isSharedFilesModalOpen, setIsSharedFilesModalOpen] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<ProjectWithStats | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isRegeneratingToken, setIsRegeneratingToken] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isApplyingTemplate, setIsApplyingTemplate] = useState(false);

  // Reports state
  const [isGenerateReportModalOpen, setIsGenerateReportModalOpen] = useState(false);
  const [reports, setReports] = useState<PortalReport[]>([]);
  const [_isLoadingReports, setIsLoadingReports] = useState(false);

  // Project form state
  const [projectName, setProjectName] = useState('');
  const [projectType, setProjectType] = useState<ProjectType>('consulting');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectDueDate, setProjectDueDate] = useState('');
  const [isSavingProject, setIsSavingProject] = useState(false);

  // File browser state
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [diagnosticForms, setDiagnosticForms] = useState<(DiagnosticForm & { responseCount?: number; portal?: FormPortal })[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const [sortBy, setSortBy] = useState<SortBy>('name');
  const [refreshKey, setRefreshKey] = useState(0);

  // Document generation modal state
  const [isCreateDocumentModalOpen, setIsCreateDocumentModalOpen] = useState(false);

  // Refs for click outside
  const portalMenuRef = useRef<HTMLDivElement>(null);
  const settingsMenuRef = useRef<HTMLDivElement>(null);

  // Sync currentFolderId with global store for upload modal
  useEffect(() => {
    setGlobalCurrentFolderId(currentFolderId);
  }, [currentFolderId, setGlobalCurrentFolderId]);

  // Sync projectId and clientId with global store for CreateFolderModal
  useEffect(() => {
    setGlobalCurrentProjectId(selectedProject?.id || null);
  }, [selectedProject?.id, setGlobalCurrentProjectId]);

  useEffect(() => {
    setGlobalCurrentClientId(currentClient?.id || null);
  }, [currentClient?.id, setGlobalCurrentClientId]);

  // Sync files with global store for MoveToFolderModal
  useEffect(() => {
    setGlobalFiles(files as any);
  }, [files, setGlobalFiles]);

  // Reload files when modals close (upload, create folder, delete, move)
  const prevUploadModalOpen = useRef(isUploadModalOpen);
  const prevCreateFolderModalOpen = useRef(isCreateFolderModalOpen);
  const prevDeleteConfirmOpen = useRef(isDeleteConfirmOpen);
  const prevMoveModalOpen = useRef(isMoveModalOpen);
  useEffect(() => {
    const uploadClosed = prevUploadModalOpen.current && !isUploadModalOpen;
    const createFolderClosed = prevCreateFolderModalOpen.current && !isCreateFolderModalOpen;
    const deleteClosed = prevDeleteConfirmOpen.current && !isDeleteConfirmOpen;
    const moveClosed = prevMoveModalOpen.current && !isMoveModalOpen;

    if (uploadClosed || createFolderClosed || deleteClosed || moveClosed) {
      // Modal just closed, reload data
      setRefreshKey((prev) => prev + 1);
    }

    prevUploadModalOpen.current = isUploadModalOpen;
    prevCreateFolderModalOpen.current = isCreateFolderModalOpen;
    prevDeleteConfirmOpen.current = isDeleteConfirmOpen;
    prevMoveModalOpen.current = isMoveModalOpen;
  }, [isUploadModalOpen, isCreateFolderModalOpen, isDeleteConfirmOpen, isMoveModalOpen]);

  // Click outside handlers
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (portalMenuRef.current && !portalMenuRef.current.contains(event.target as Node)) {
        setIsPortalMenuOpen(false);
      }
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target as Node)) {
        setIsSettingsMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load client data
  useEffect(() => {
    if (id) {
      loadClient(id);
      loadProjects(id);
      loadReports(id);
    }
    fetchTemplates();
    return () => setCurrentClient(null);
  }, [id]);

  const loadClient = async (clientId: string) => {
    setIsLoading(true);
    try {
      const response = await clientsApi.get(clientId);
      setCurrentClient(response.client as ClientDetail);
    } catch (error) {
      console.error('Error loading client:', error);
      toastRef.current.error('Failed to load client');
      navigate('/clients');
    } finally {
      setIsLoading(false);
    }
  };

  const loadProjects = async (clientId: string) => {
    setIsLoadingProjects(true);
    try {
      const response = await projectsApi.list({ clientId });
      setProjects(response.projects);
    } catch (error) {
      console.error('Error loading projects:', error);
      toastRef.current.error('Error al cargar proyectos');
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const loadReports = async (clientId: string) => {
    setIsLoadingReports(true);
    try {
      const response = await portalReportsApi.list(clientId);
      setReports(response.reports);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setIsLoadingReports(false);
    }
  };

  // Load files for selected project - useEffect with proper dependencies
  useEffect(() => {
    if (!selectedProject) {
      // When no project selected, show client breadcrumb
      if (currentClient) {
        setBreadcrumb([{ id: null, name: currentClient.name, type: 'client' }]);
      }
      setDiagnosticForms([]);
      return;
    }

    let cancelled = false;

    const loadFiles = async () => {
      setIsLoadingFiles(true);
      try {
        const foldersRes = await foldersApi.list(currentFolderId, undefined, selectedProject.id);
        if (cancelled) return;
        setFolders(foldersRes.folders);

        if (currentFolderId) {
          const filesRes = await filesApi.list({
            folderId: currentFolderId,
            trashed: false,
          });
          if (cancelled) return;
          setFiles(filesRes.files);
        } else {
          setFiles([]);
        }

        // Load diagnostic forms if this is the "Diagnósticos" project
        if (selectedProject.name === 'Diagnósticos' && !currentFolderId && currentClient?.id) {
          const diagResponse = await diagnosticsApi.getAll({ clientId: currentClient.id });
          if (cancelled) return;
          setDiagnosticForms(diagResponse.forms as any);
        } else {
          setDiagnosticForms([]);
        }

        // Build breadcrumb
        const crumbs: BreadcrumbItem[] = [
          { id: null, name: currentClient?.name || 'Cliente', type: 'client' },
          { id: selectedProject.id, name: selectedProject.name, type: 'project' },
        ];

        if (currentFolderId) {
          const breadcrumbRes = await foldersApi.getBreadcrumb(currentFolderId);
          if (cancelled) return;
          crumbs.push(
            ...breadcrumbRes.breadcrumb.map((b: any) => ({
              ...b,
              type: 'folder' as const,
            }))
          );
        }

        setBreadcrumb(crumbs);
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading files:', error);
        toastRef.current.error('Error al cargar archivos');
      } finally {
        if (!cancelled) {
          setIsLoadingFiles(false);
        }
      }
    };

    loadFiles();

    return () => {
      cancelled = true;
    };
  }, [selectedProject?.id, currentFolderId, currentClient?.name, currentClient?.id, refreshKey]);

  // Handlers
  const handleCopyLink = () => {
    if (currentClient?.portal?.token) {
      const url = `${window.location.origin}/p/${currentClient.portal.token}`;
      navigator.clipboard.writeText(url);
      toastRef.current.success('Link copiado');
    }
    setIsPortalMenuOpen(false);
  };

  const handleViewPortal = () => {
    if (currentClient?.portal?.token) {
      window.open(`/p/${currentClient.portal.token}`, '_blank');
    }
    setIsPortalMenuOpen(false);
  };

  const handleRegenerateToken = async () => {
    if (!currentClient || !id) return;
    setIsRegeneratingToken(true);
    try {
      const response = await portalsApi.regenerateToken(id);
      updateCurrentClientPortal({ token: response.token });
      toastRef.current.success('Link regenerado');
    } catch (error: any) {
      toastRef.current.error(error.message || 'Error');
    } finally {
      setIsRegeneratingToken(false);
      setIsPortalMenuOpen(false);
    }
  };

  const handleSavePassword = async () => {
    if (!id) return;
    setIsSavingPassword(true);
    try {
      if (newPassword) {
        await portalsApi.update(id, { password: newPassword });
        updateCurrentClientPortal({ hasPassword: true });
        toastRef.current.success('Contraseña establecida');
      } else {
        await portalsApi.update(id, { removePassword: true });
        updateCurrentClientPortal({ hasPassword: false });
        toastRef.current.success('Contraseña eliminada');
      }
      setIsPasswordModalOpen(false);
      setNewPassword('');
    } catch (error: any) {
      toastRef.current.error(error.message || 'Error');
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleRemoveFile = async (portalFileId: string) => {
    if (!id) return;
    try {
      await portalsApi.removeFile(id, portalFileId);
      removePortalFile(portalFileId);
      toastRef.current.success('Archivo eliminado del portal');
    } catch (error: any) {
      toastRef.current.error(error.message || 'Error');
    }
  };

  const handleFilesAdded = (files: any[]) => {
    setPortalFiles(files);
    setIsFileSelectorOpen(false);
  };

  const handleDeleteClient = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await clientsApi.delete(id);
      removeClient(id);
      toastRef.current.success('Cliente eliminado');
      navigate('/clients');
    } catch (error: any) {
      toastRef.current.error(error.message || 'Error');
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedProject || !selectedTemplateId) return;
    setIsApplyingTemplate(true);
    try {
      const result = await applyTemplate(selectedTemplateId, { projectId: selectedProject.id });
      toastRef.current.success(`${result.foldersCreated} carpetas creadas`);
      setIsTemplateModalOpen(false);
      setSelectedTemplateId(null);
      setRefreshKey((prev) => prev + 1);
    } catch (error: any) {
      toastRef.current.error(error.message || 'Error');
    } finally {
      setIsApplyingTemplate(false);
    }
  };

  const handleSaveProject = async () => {
    if (!id || !projectName.trim()) return;
    setIsSavingProject(true);
    try {
      if (editingProject) {
        const response = await projectsApi.update(editingProject.id, {
          name: projectName,
          type: projectType,
          description: projectDescription || undefined,
          dueDate: projectDueDate || null,
        });
        setProjects((prev) =>
          prev.map((p) => (p.id === editingProject.id ? { ...response.project, folderCount: p.folderCount, fileCount: p.fileCount } : p))
        );
        toastRef.current.success('Proyecto actualizado');
      } else {
        await projectsApi.create({
          clientId: id,
          name: projectName,
          type: projectType,
          description: projectDescription || undefined,
          dueDate: projectDueDate || undefined,
        });
        // Reload projects list to get fresh data
        await loadProjects(id);
        toastRef.current.success('Proyecto creado');
      }
      closeProjectModal();
    } catch (error: any) {
      toastRef.current.error(error.message || 'Error');
    } finally {
      setIsSavingProject(false);
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      await projectsApi.delete(projectId);
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      if (selectedProject?.id === projectId) {
        setSelectedProject(null);
        setCurrentFolderId(null);
      }
      toastRef.current.success('Proyecto eliminado');
    } catch (error: any) {
      toastRef.current.error(error.message || 'Error');
    }
  };

  const handleUpdateProjectStatus = async (projectId: string, status: ProjectStatus) => {
    try {
      const response = await projectsApi.update(projectId, { status });
      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, status: response.project.status } : p))
      );
      toastRef.current.success('Estado actualizado');
    } catch (error: any) {
      toastRef.current.error(error.message || 'Error');
    }
  };

  const handleTogglePortalVisibility = async (projectId: string, visible: boolean) => {
    try {
      await projectsApi.togglePortalVisibility(projectId, visible);
      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, portalVisible: visible } : p))
      );
      toastRef.current.success(visible ? 'Proyecto visible en portal' : 'Proyecto oculto del portal');
    } catch (error: any) {
      toastRef.current.error(error.message || 'Error al cambiar visibilidad');
    }
  };

  const openProjectModal = (project?: ProjectWithStats) => {
    if (project) {
      setEditingProject(project);
      setProjectName(project.name);
      setProjectType(project.type as ProjectType);
      setProjectDescription(project.description || '');
      setProjectDueDate(project.dueDate || '');
    } else {
      setEditingProject(null);
      setProjectName('');
      setProjectType('consulting');
      setProjectDescription('');
      setProjectDueDate('');
    }
    setIsProjectModalOpen(true);
  };

  const closeProjectModal = () => {
    setIsProjectModalOpen(false);
    setEditingProject(null);
    setProjectName('');
    setProjectType('consulting');
    setProjectDescription('');
    setProjectDueDate('');
  };

  const handleProjectClick = (project: ProjectWithStats) => {
    setSelectedProject(project);
    setCurrentFolderId(null);
  };

  const handleFolderClick = (folderId: string) => {
    setCurrentFolderId(folderId);
  };

  const handleBreadcrumbClick = (item: BreadcrumbItem) => {
    if (item.type === 'client') {
      setSelectedProject(null);
      setCurrentFolderId(null);
    } else if (item.type === 'project') {
      setCurrentFolderId(null);
    } else {
      setCurrentFolderId(item.id);
    }
  };

  const handleDownload = async (fileId: string) => {
    try {
      const { downloadUrl } = await filesApi.getDownloadUrl(fileId);
      window.open(downloadUrl, '_blank');
    } catch {
      toastRef.current.error('Error al descargar');
    }
  };

  // Handle file click - open editor for markdown, download for others
  const handleFileClick = (file: FileItem) => {
    const isMarkdown = file.mimeType === 'text/markdown' || file.name.endsWith('.md');
    if (isMarkdown) {
      navigate(`/documents/${file.id}`);
    } else {
      handleDownload(file.id);
    }
  };

  const toggleSort = () => {
    setSortBy(sortBy === 'name' ? 'updatedAt' : 'name');
  };

  // Context menu handler for files
  const handleFileContextMenu = (e: React.MouseEvent, fileId: string) => {
    e.preventDefault();
    e.stopPropagation();
    openContextMenu({ id: fileId, type: 'file' }, { x: e.clientX, y: e.clientY });
  };

  // Sort items - memoized to prevent recalculation on every render
  const sortedProjects = useMemo(() =>
    [...projects].sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    }), [projects, sortBy]
  );

  const sortedFolders = useMemo(() =>
    [...folders].sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    }), [folders, sortBy]
  );

  const sortedFiles = useMemo(() =>
    [...files].sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }), [files, sortBy]
  );

  const isEmpty = selectedProject ? folders.length === 0 && files.length === 0 && diagnosticForms.length === 0 : projects.length === 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!currentClient) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header - Minimalist */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate('/clients')}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-500 dark:text-slate-400" />
          </motion.button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {currentClient.name}
            </h1>
            {currentClient.company && (
              <p className="text-sm text-gray-500 dark:text-slate-400">{currentClient.company}</p>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {/* Portal Menu */}
          <div ref={portalMenuRef} className="relative">
            <button
              onClick={() => setIsPortalMenuOpen(!isPortalMenuOpen)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-slate-200 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
            >
              <LinkIcon className="w-4 h-4" />
              Portal
              <ChevronDownIcon className={clsx('w-4 h-4 transition-transform', isPortalMenuOpen && 'rotate-180')} />
            </button>

            <AnimatePresence>
              {isPortalMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 z-50 mt-2 w-56 py-1 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700"
                >
                  <button
                    onClick={handleCopyLink}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    <ClipboardDocumentIcon className="w-4 h-4" />
                    Copiar link
                  </button>
                  <button
                    onClick={handleViewPortal}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    <EyeIcon className="w-4 h-4" />
                    Ver portal
                  </button>
                  <div className="my-1 border-t border-gray-200 dark:border-slate-700" />
                  <button
                    onClick={() => {
                      setIsPasswordModalOpen(true);
                      setIsPortalMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    {currentClient.portal?.hasPassword ? (
                      <LockClosedIcon className="w-4 h-4 text-green-500" />
                    ) : (
                      <LockOpenIcon className="w-4 h-4" />
                    )}
                    {currentClient.portal?.hasPassword ? 'Cambiar contraseña' : 'Agregar contraseña'}
                  </button>
                  <button
                    onClick={handleRegenerateToken}
                    disabled={isRegeneratingToken}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-50"
                  >
                    <ArrowPathIcon className={clsx('w-4 h-4', isRegeneratingToken && 'animate-spin')} />
                    Regenerar link
                  </button>
                  <div className="my-1 border-t border-gray-200 dark:border-slate-700" />
                  <button
                    onClick={() => {
                      setIsSharedFilesModalOpen(true);
                      setIsPortalMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    <DocumentDuplicateIcon className="w-4 h-4" />
                    Archivos compartidos
                    {currentClient.portalFiles.length > 0 && (
                      <span className="ml-auto text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 px-1.5 py-0.5 rounded">
                        {currentClient.portalFiles.length}
                      </span>
                    )}
                  </button>
                  <div className="my-1 border-t border-gray-200 dark:border-slate-700" />
                  <button
                    onClick={() => {
                      setIsGenerateReportModalOpen(true);
                      setIsPortalMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    <SparklesIcon className="w-4 h-4 text-primary-500" />
                    Generar Reporte IA
                    {reports.filter(r => r.status === 'published').length > 0 && (
                      <span className="ml-auto text-xs bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded">
                        Publicado
                      </span>
                    )}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Settings Menu */}
          <div ref={settingsMenuRef} className="relative">
            <button
              onClick={() => setIsSettingsMenuOpen(!isSettingsMenuOpen)}
              className="p-2 text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <Cog6ToothIcon className="w-5 h-5" />
            </button>

            <AnimatePresence>
              {isSettingsMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 z-50 mt-2 w-56 py-1 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700"
                >
                  <div className="my-1 border-t border-gray-200 dark:border-slate-700" />
                  <button
                    onClick={() => {
                      setIsDeleteModalOpen(true);
                      setIsSettingsMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Eliminar cliente
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm">
        {breadcrumb.map((item, index) => (
          <div key={`${item.type}-${item.id ?? 'root'}`} className="flex items-center gap-1">
            {index > 0 && (
              <ChevronRightIcon className="w-4 h-4 text-gray-400 dark:text-slate-500" />
            )}
            <button
              onClick={() => handleBreadcrumbClick(item)}
              className={clsx(
                'flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors',
                index === breadcrumb.length - 1
                  ? 'text-gray-900 dark:text-white font-medium'
                  : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700'
              )}
            >
              {item.type === 'client' && <HomeIcon className="w-4 h-4" />}
              {item.type === 'project' && <BriefcaseIcon className="w-4 h-4" />}
              <span className="truncate max-w-40">{item.name}</span>
            </button>
          </div>
        ))}
      </nav>

      {/* Reports Section - Show when there are draft reports to review */}
      {reports.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-slate-300">
              <DocumentTextIcon className="w-4 h-4" />
              Reportes de Documentación
              {reports.filter(r => r.status === 'draft').length > 0 && (
                <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-1.5 py-0.5 rounded">
                  {reports.filter(r => r.status === 'draft').length} pendiente(s)
                </span>
              )}
            </h3>
          </div>
          {reports.map((report) => (
            <DocumentationReport
              key={report.id}
              clientId={id!}
              report={report}
              onUpdate={(updated) => {
                setReports((prev) =>
                  prev.map((r) => (r.id === updated.id ? updated : r))
                );
              }}
              onDelete={() => {
                setReports((prev) => prev.filter((r) => r.id !== report.id));
              }}
            />
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        {/* Left side - sort */}
        <button
          onClick={toggleSort}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
        >
          <ArrowsUpDownIcon className="w-4 h-4" />
          Ordenar por {sortBy === 'name' ? 'Nombre' : 'Fecha'}
        </button>

        {/* Right side - action buttons */}
        <div className="flex items-center gap-2">
          {!selectedProject ? (
            <Button variant="secondary" size="sm" onClick={() => openProjectModal()}>
              <FolderPlusIcon className="w-4 h-4" />
              Nuevo Proyecto
            </Button>
          ) : (
            <>
              {currentFolderId && (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setIsCreateDocumentModalOpen(true)}
                  >
                    <SparklesIcon className="w-4 h-4" />
                    Crear Documento
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => openUploadModal()}>
                    <CloudArrowUpIcon className="w-4 h-4" />
                    Upload
                  </Button>
                </>
              )}
              <Button variant="secondary" size="sm" onClick={openCreateFolderModal}>
                <FolderPlusIcon className="w-4 h-4" />
                Nueva Carpeta
              </Button>
              {!currentFolderId && (
                <Button variant="secondary" size="sm" onClick={() => setIsTemplateModalOpen(true)}>
                  <DocumentDuplicateIcon className="w-4 h-4" />
                  Usar Plantilla
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Content */}
      {(isLoadingProjects && !selectedProject) || (isLoadingFiles && selectedProject) ? (
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : isEmpty ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center justify-center py-20 text-center"
        >
          <div className="w-20 h-20 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
            {selectedProject ? (
              <FolderIcon className="w-10 h-10 text-gray-400 dark:text-slate-500" />
            ) : (
              <BriefcaseIcon className="w-10 h-10 text-gray-400 dark:text-slate-500" />
            )}
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
            {selectedProject
              ? currentFolderId
                ? 'Esta carpeta esta vacia'
                : 'Sin carpetas'
              : 'Sin proyectos'}
          </h3>
          <p className="text-gray-500 dark:text-slate-400 max-w-sm mb-4">
            {selectedProject
              ? currentFolderId
                ? 'Sube archivos o crea subcarpetas'
                : 'Crea carpetas manualmente o usa una plantilla predefinida'
              : 'Crea un proyecto para organizar el trabajo de este cliente'}
          </p>
          {!selectedProject && (
            <Button variant="secondary" onClick={() => openProjectModal()}>
              <FolderPlusIcon className="w-4 h-4" />
              Nuevo Proyecto
            </Button>
          )}
          {selectedProject && !currentFolderId && (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={openCreateFolderModal}>
                <FolderPlusIcon className="w-4 h-4" />
                Nueva Carpeta
              </Button>
              <Button variant="ghost" onClick={() => setIsTemplateModalOpen(true)}>
                <DocumentDuplicateIcon className="w-4 h-4" />
                Usar Plantilla
              </Button>
            </div>
          )}
        </motion.div>
      ) : !selectedProject ? (
        // Projects view - List only
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-1">
          {sortedProjects.map((project) => (
            <motion.div
              key={project.id}
              variants={itemVariants}
              className="flex items-center gap-4 px-4 py-3 rounded-xl cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-slate-800 group"
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${typeColors[project.type as ProjectType]}15` }}
                onClick={() => handleProjectClick(project)}
              >
                <BriefcaseIcon className="w-6 h-6" style={{ color: typeColors[project.type as ProjectType] }} />
              </div>
              <div className="flex-1 min-w-0" onClick={() => handleProjectClick(project)}>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-900 dark:text-white truncate">{project.name}</p>
                  <span className={clsx('text-xs px-2 py-0.5 rounded-full', statusColors[project.status as ProjectStatus])}>
                    {projectStatusLabels[project.status as ProjectStatus]}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  {projectTypeLabels[project.type as ProjectType]} • {project.folderCount || 0} carpetas • {formatDate(project.updatedAt)}
                </p>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTogglePortalVisibility(project.id, !project.portalVisible);
                  }}
                  className={clsx(
                    'p-2 rounded-lg transition-colors',
                    project.portalVisible
                      ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/30 dark:text-primary-400'
                      : 'text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300'
                  )}
                  title={project.portalVisible ? 'Ocultar del portal' : 'Mostrar en portal'}
                >
                  <LinkIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openProjectModal(project);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteProject(project.id);
                  }}
                  className="p-2 text-gray-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-400 dark:text-slate-500" onClick={() => handleProjectClick(project)} />
            </motion.div>
          ))}
        </motion.div>
      ) : (
        // Folders/Files view (when a project is selected) - List only
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-1">
          {sortedFolders.map((folder) => (
            <motion.div
              key={folder.id}
              variants={itemVariants}
              onClick={() => handleFolderClick(folder.id)}
              className="flex items-center gap-4 px-4 py-3 rounded-xl cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-slate-800"
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: folder.color ? `${folder.color}20` : 'rgba(99, 102, 241, 0.1)' }}
              >
                <FolderSolidIcon className="w-6 h-6" style={{ color: folder.color || '#6366f1' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white truncate">{folder.name}</p>
                <p className="text-sm text-gray-500 dark:text-slate-400">Carpeta • {formatDate(folder.updatedAt)}</p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-400 dark:text-slate-500" />
            </motion.div>
          ))}
          {/* Diagnostic Forms */}
          {diagnosticForms.map((form) => (
            <motion.div
              key={form.id}
              variants={itemVariants}
              onClick={() => navigate(`/diagnostics/${form.id}`)}
              className="flex items-center gap-4 px-4 py-3 rounded-xl cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-slate-800 group"
            >
              <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <ClipboardDocumentListIcon className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-900 dark:text-white truncate">{form.title}</p>
                  <span className={clsx(
                    'text-xs px-2 py-0.5 rounded-full',
                    form.status === 'published'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : form.status === 'draft'
                        ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400'
                  )}>
                    {form.status === 'published' ? 'Publicado' : form.status === 'draft' ? 'Borrador' : 'Archivado'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  Formulario • {form.responseCount || 0} respuestas • {formatDate(form.updatedAt)}
                </p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-400 dark:text-slate-500" />
            </motion.div>
          ))}
          {sortedFiles.map((file) => (
            <motion.div
              key={file.id}
              variants={itemVariants}
              onClick={() => handleFileClick(file)}
              onContextMenu={(e) => handleFileContextMenu(e, file.id)}
              className="flex items-center gap-4 px-4 py-3 rounded-xl cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-slate-800"
            >
              <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <DocumentIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white truncate">{file.name}</p>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  {formatFileSize(file.size)} • {formatDate(file.createdAt)}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Password Modal */}
      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => {
          setIsPasswordModalOpen(false);
          setNewPassword('');
        }}
        title={currentClient.portal?.hasPassword ? 'Cambiar contraseña' : 'Agregar contraseña'}
        description="Protege el portal con una contraseña"
        size="sm"
      >
        <div className="space-y-4">
          <Input
            type="password"
            label="Contraseña"
            placeholder={currentClient.portal?.hasPassword ? 'Nueva contraseña (vacio para eliminar)' : 'Contraseña'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            leftIcon={<LockClosedIcon className="w-5 h-5" />}
          />
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => setIsPasswordModalOpen(false)} className="flex-1">
              Cancelar
            </Button>
            <Button onClick={handleSavePassword} isLoading={isSavingPassword} className="flex-1">
              {newPassword ? 'Guardar' : 'Eliminar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Shared Files Modal */}
      <Modal
        isOpen={isSharedFilesModalOpen}
        onClose={() => setIsSharedFilesModalOpen(false)}
        title="Archivos compartidos"
        description="Archivos visibles en el portal del cliente"
        size="md"
      >
        <div className="space-y-4">
          {currentClient.portalFiles.length === 0 ? (
            <div className="text-center py-8">
              <DocumentIcon className="w-12 h-12 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-slate-400 mb-4">Sin archivos compartidos</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {currentClient.portalFiles.map((portalFile) => (
                <div
                  key={portalFile.id}
                  className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg group"
                >
                  <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <DocumentIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">
                      {portalFile.displayName || portalFile.file?.name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-slate-400">
                      {formatFileSize(portalFile.file?.size || 0)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveFile(portalFile.id)}
                    className="p-2 text-gray-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <Button variant="secondary" className="w-full" onClick={() => {
            setIsSharedFilesModalOpen(false);
            setIsFileSelectorOpen(true);
          }}>
            <PlusIcon className="w-4 h-4" />
            Agregar archivos
          </Button>
        </div>
      </Modal>

      {/* File Selector Modal */}
      <PortalFileSelector
        isOpen={isFileSelectorOpen}
        onClose={() => setIsFileSelectorOpen(false)}
        clientId={id!}
        currentFileIds={currentClient.portalFiles.map((f) => f.fileId)}
        onFilesAdded={handleFilesAdded}
      />

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Eliminar cliente"
        description="Esta accion no se puede deshacer"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">
              Al eliminar a <strong>{currentClient.name}</strong> se borrara su portal, archivos compartidos y toda la
              actividad asociada.
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)} className="flex-1">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDeleteClient} isLoading={isDeleting} className="flex-1">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Template Modal */}
      <Modal
        isOpen={isTemplateModalOpen}
        onClose={() => {
          setIsTemplateModalOpen(false);
          setSelectedTemplateId(null);
        }}
        title="Aplicar plantilla"
        description="Crea una estructura de carpetas para el proyecto"
        size="md"
      >
        <div className="space-y-4">
          {templates.length === 0 ? (
            <p className="text-gray-500 dark:text-slate-400 text-center py-4">No hay plantillas disponibles</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplateId(template.id)}
                  className={clsx(
                    'w-full p-4 rounded-lg border text-left transition-colors',
                    selectedTemplateId === template.id
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                  )}
                >
                  <p className="font-medium text-gray-900 dark:text-white">
                    {template.name}
                    {template.isSystem && (
                      <span className="ml-2 text-xs px-2 py-0.5 bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 rounded">
                        Sistema
                      </span>
                    )}
                  </p>
                  {template.description && (
                    <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">{template.description}</p>
                  )}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" onClick={() => setIsTemplateModalOpen(false)} className="flex-1">
              Cancelar
            </Button>
            <Button onClick={handleApplyTemplate} disabled={!selectedTemplateId} isLoading={isApplyingTemplate} className="flex-1">
              <FolderPlusIcon className="w-4 h-4" />
              Aplicar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Project Modal */}
      <Modal
        isOpen={isProjectModalOpen}
        onClose={closeProjectModal}
        title={editingProject ? 'Editar proyecto' : 'Nuevo proyecto'}
        description={editingProject ? 'Modifica los datos del proyecto' : 'Crea un nuevo proyecto para este cliente'}
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Nombre del proyecto"
            placeholder="Ej: Declaracion Anual 2024"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              Tipo de proyecto
            </label>
            <div className="grid grid-cols-3 gap-2">
              {(Object.keys(projectTypeLabels) as ProjectType[]).map((type) => (
                <button
                  key={type}
                  onClick={() => setProjectType(type)}
                  className={clsx(
                    'px-3 py-2 text-sm rounded-lg border transition-colors',
                    projectType === type
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500 text-gray-700 dark:text-slate-300'
                  )}
                >
                  {projectTypeLabels[type]}
                </button>
              ))}
            </div>
          </div>

          <Input
            label="Descripcion (opcional)"
            placeholder="Breve descripcion del proyecto"
            value={projectDescription}
            onChange={(e) => setProjectDescription(e.target.value)}
          />

          <Input
            type="date"
            label="Fecha limite (opcional)"
            value={projectDueDate}
            onChange={(e) => setProjectDueDate(e.target.value)}
          />

          {editingProject && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Estado
              </label>
              <div className="grid grid-cols-4 gap-2">
                {(Object.keys(projectStatusLabels) as ProjectStatus[]).map((status) => (
                  <button
                    key={status}
                    onClick={() => handleUpdateProjectStatus(editingProject.id, status)}
                    className={clsx(
                      'px-3 py-2 text-xs rounded-lg border transition-colors flex items-center justify-center gap-1',
                      editingProject.status === status
                        ? statusColors[status]
                        : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500 text-gray-700 dark:text-slate-300'
                    )}
                  >
                    {statusIcons[status]}
                    {projectStatusLabels[status]}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button variant="secondary" onClick={closeProjectModal} className="flex-1">
              Cancelar
            </Button>
            <Button onClick={handleSaveProject} disabled={!projectName.trim()} isLoading={isSavingProject} className="flex-1">
              {editingProject ? 'Guardar' : 'Crear proyecto'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Generate Report Modal */}
      <GenerateReportModal
        isOpen={isGenerateReportModalOpen}
        onClose={() => setIsGenerateReportModalOpen(false)}
        clientId={id!}
        clientName={currentClient.name}
        onGenerated={(report) => {
          setReports((prev) => [report, ...prev]);
        }}
      />

      {/* Create Document Modal */}
      <CreateDocumentModal
        isOpen={isCreateDocumentModalOpen}
        onClose={() => setIsCreateDocumentModalOpen(false)}
        folderId={currentFolderId}
      />

      {/* Context Menu and related modals */}
      <ContextMenuWrapper />
      <RenameModal />
      <CreateLinkModal />
      <DeleteConfirmDialog />
      <MoveToFolderModal />
    </div>
  );
}
